import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  CheckCircle2,
  X,
  Image,
  Plus,
  MessageSquare,
  Clock,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useApp } from '../context';
import { supportApi, ApiSupportTicket } from '../lib/api';
import { registerCacheCleanup, hasChanged } from '../lib/moduleCache';
import { showToast } from '../lib/toast';

const SUPPORT_TOPICS = [
  { label: 'Bug Report', value: 'bug_report' },
  { label: 'Feature Request', value: 'feature_request' },
  { label: 'Account Issue', value: 'account_issue' },
  { label: 'Notifications', value: 'notifications' },
  { label: 'Feedback', value: 'feedback' },
  { label: 'Other', value: 'other' },
];

const TOPIC_LABELS: Record<string, string> = {
  bug_report: 'Bug Report',
  feature_request: 'Feature Request',
  account_issue: 'Account Issue',
  notifications: 'Notifications',
  feedback: 'Feedback',
  other: 'Other',
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'bg-amber-50 text-amber-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-50 text-blue-700' },
  resolved: { label: 'Resolved', color: 'bg-emerald-50 text-emerald-700' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-500' },
};

const MAX_ATTACHMENTS = 3;

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function TicketCard({ ticket, onClick }: { ticket: ApiSupportTicket; onClick: () => void }) {
  const status = STATUS_LABELS[ticket.status] || STATUS_LABELS.open;
  const preview = ticket.lastMessageText || ticket.message;
  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-2xl border border-slate-100 p-4 text-left hover:border-slate-200 transition-colors relative"
    >
      {ticket.hasUnreadAdminReply && (
        <div className="absolute top-3 right-3 size-2.5 bg-red-500 rounded-full" />
      )}
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className={cn('text-[10px] font-bold uppercase px-2 py-0.5 rounded-full', status.color)}
        >
          {status.label}
        </span>
        <span className="text-[10px] font-semibold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
          {TOPIC_LABELS[ticket.topic] || ticket.topic}
        </span>
      </div>
      <p className="text-sm font-bold text-slate-900 line-clamp-1">{ticket.message}</p>
      <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{preview}</p>
      <div className="flex items-center gap-1 mt-2 text-[10px] text-slate-400">
        <Clock size={10} />
        <span>{formatTime(ticket.createdAt)}</span>
      </div>
    </button>
  );
}

// Module-level cache so re-mounts don't flash loading spinner
let _openTicketsCache: ApiSupportTicket[] | null = null;
let _closedTicketsCache: ApiSupportTicket[] | null = null;
let _closedCountCache: number | null = null;
registerCacheCleanup(() => {
  _openTicketsCache = null;
  _closedTicketsCache = null;
  _closedCountCache = null;
});

export default function HelpSupport() {
  const navigate = useNavigate();
  const { state } = useApp();
  const isGuest = state.user?.isGuest;

  // Ticket list state
  const [openTickets, setOpenTickets] = useState<ApiSupportTicket[]>(_openTicketsCache || []);
  const [closedTickets, setClosedTickets] = useState<ApiSupportTicket[]>(_closedTicketsCache || []);
  const [loadingTickets, setLoadingTickets] = useState(!isGuest && !_openTicketsCache);
  const [loadingClosed, setLoadingClosed] = useState(false);
  const [closedFetched, setClosedFetched] = useState(!!_closedTicketsCache);
  const [closedCount, setClosedCount] = useState<number | null>(_closedCountCache);
  const [showNewForm, setShowNewForm] = useState(false);
  const [closedExpanded, setClosedExpanded] = useState(
    () => sessionStorage.getItem('nudge_closed_tickets_expanded') === 'true',
  );

  // New ticket form state
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isGuest) return;
    supportApi
      .list({ status: 'active' })
      .then((res) => {
        if (hasChanged(_openTicketsCache, res.data)) setOpenTickets(res.data);
        _openTicketsCache = res.data;
        if (res.closedCount !== undefined) {
          if (_closedCountCache !== res.closedCount) setClosedCount(res.closedCount);
          _closedCountCache = res.closedCount;
        }
      })
      .catch(() => {})
      .finally(() => setLoadingTickets(false));
  }, [isGuest]);

  const fetchClosedTickets = () => {
    if (closedFetched) return;
    setLoadingClosed(true);
    supportApi
      .list({ status: 'closed' })
      .then((res) => {
        if (hasChanged(_closedTicketsCache, res.data)) setClosedTickets(res.data);
        _closedTicketsCache = res.data;
        setClosedFetched(true);
      })
      .catch(() => {})
      .finally(() => setLoadingClosed(false));
  };

  // If closed section was previously expanded, re-fetch on mount
  useEffect(() => {
    if (closedExpanded && !closedFetched && !isGuest) fetchClosedTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExpandClosed = () => {
    const next = !closedExpanded;
    setClosedExpanded(next);
    sessionStorage.setItem('nudge_closed_tickets_expanded', String(next));
    if (next) fetchClosedTickets();
  };

  const handleAddPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_ATTACHMENTS - attachments.length;
    const toAdd = files.slice(0, remaining);

    setAttachments((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPreviews((prev) => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTopic || !message.trim()) return;

    if (isGuest) {
      showToast('Create an account to submit feedback', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const ticket = await supportApi.create(
        { topic: selectedTopic, message: message.trim() },
        attachments.length > 0 ? attachments : undefined,
      );
      setIsSubmitted(true);
      setOpenTickets((prev) => [ticket, ...prev]);
      setTimeout(() => {
        setShowNewForm(false);
        setIsSubmitted(false);
        setSelectedTopic(null);
        setMessage('');
        setAttachments([]);
        setPreviews([]);
      }, 1500);
    } catch {
      showToast('Failed to send message. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-dvh w-full flex-col bg-[#f8f5f5] overflow-hidden">
      {/* Header */}
      <div className="shrink-0 flex items-center p-4 pb-2 justify-between bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={() => (showNewForm ? setShowNewForm(false) : navigate(-1))}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-lg font-bold text-slate-900">
          {showNewForm ? 'New Ticket' : 'Support & Feedback'}
        </h1>
        <div className="size-10" />
      </div>

      {showNewForm ? (
        /* ─── New Ticket Form ─── */
        <div className="px-5 py-8 flex-1 flex flex-col overflow-y-auto">
          {isSubmitted ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in duration-300">
              <div className="size-20 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 size={40} className="text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Message Sent!</h2>
              <p className="text-slate-500">Our support team will get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">How can we help?</h2>
                <p className="text-slate-500 text-sm">
                  Select a category and let us know what's on your mind.
                </p>
              </div>

              {/* Topic Tags */}
              <div className="mb-8">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block">
                  Topic
                </label>
                <div className="flex flex-wrap gap-2">
                  {SUPPORT_TOPICS.map((topic) => (
                    <button
                      key={topic.value}
                      type="button"
                      onClick={() => setSelectedTopic(topic.value)}
                      className={cn(
                        'px-4 py-2 rounded-full text-sm font-semibold transition-all',
                        selectedTopic === topic.value
                          ? 'bg-[#fa8585] text-white shadow-md shadow-[#fa8585]/20'
                          : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-[#fa8585]/50 hover:bg-white',
                      )}
                    >
                      {topic.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div className="flex flex-col min-h-40 mb-4">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block">
                  Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your issue or feedback in detail..."
                  className="flex-1 w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-900 focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all resize-none"
                />
              </div>

              {/* Photo Attachments */}
              <div className="mb-6">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block">
                  Screenshots (optional)
                </label>
                <div className="flex items-center gap-3 flex-wrap">
                  {previews.map((src, idx) => (
                    <div
                      key={idx}
                      className="relative size-20 rounded-xl overflow-hidden border border-slate-200"
                    >
                      <img src={src} alt="attachment" className="size-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="absolute top-1 right-1 size-5 bg-black/60 rounded-full flex items-center justify-center"
                      >
                        <X size={12} className="text-white" />
                      </button>
                    </div>
                  ))}

                  {attachments.length < MAX_ATTACHMENTS && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="size-20 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-400 hover:border-[#fa8585]/50 hover:text-[#fa8585] transition-colors"
                    >
                      <Image size={20} />
                      <span className="text-[10px] font-bold">Add</span>
                    </button>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleAddPhoto}
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  Up to {MAX_ATTACHMENTS} images (JPEG, PNG, WebP)
                </p>
              </div>

              {/* Submit Button */}
              <div className="mt-auto pt-4">
                <button
                  type="submit"
                  disabled={!selectedTopic || !message.trim() || isSubmitting}
                  className="w-full bg-[#fa8585] hover:bg-[#fa8585]/90 disabled:opacity-40 disabled:hover:bg-[#fa8585] text-white font-bold py-4 rounded-xl shadow-lg shadow-[#fa8585]/30 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                >
                  {isSubmitting ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <Send size={18} className="translate-x-0.5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      ) : (
        /* ─── Ticket List ─── */
        <div className="px-5 py-6 flex-1 flex flex-col overflow-y-auto">
          {/* New ticket button */}
          <button
            onClick={() => {
              if (isGuest) {
                showToast('Create an account to submit feedback', 'error');
                return;
              }
              setShowNewForm(true);
            }}
            className="w-full bg-[#fa8585] hover:bg-[#fa8585]/90 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-[#fa8585]/30 flex items-center justify-center gap-2 transition-all active:scale-[0.98] mb-6"
          >
            <Plus size={18} />
            <span>New Ticket</span>
          </button>

          {/* Tickets */}
          {loadingTickets ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-slate-400" />
            </div>
          ) : openTickets.length === 0 &&
            !closedFetched &&
            (closedCount === null || closedCount === 0) ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="size-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <MessageSquare size={24} className="text-slate-300" />
              </div>
              <p className="text-slate-400 text-sm font-medium">No open tickets</p>
              <p className="text-slate-300 text-xs mt-1">Create a new ticket to get support</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Open Tickets */}
              {openTickets.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                    Your Open Tickets
                  </p>
                  <div className="space-y-3">
                    {openTickets.map((ticket) => (
                      <TicketCard
                        key={ticket.id}
                        ticket={ticket}
                        onClick={() => {
                          if (ticket.hasUnreadAdminReply) {
                            setOpenTickets((prev) =>
                              prev.map((t) =>
                                t.id === ticket.id ? { ...t, hasUnreadAdminReply: false } : t,
                              ),
                            );
                          }
                          navigate(`/support/${ticket.id}`);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Closed Tickets */}
              <div>
                <button onClick={handleExpandClosed} className="flex items-center gap-2 mb-3">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Closed Tickets
                  </p>
                  {(closedCount !== null || closedFetched) && (
                    <span className="text-xs text-slate-300 font-semibold">
                      ({closedFetched ? closedTickets.length : closedCount})
                    </span>
                  )}
                  <ChevronDown
                    size={14}
                    className={cn(
                      'text-slate-400 transition-transform',
                      closedExpanded && 'rotate-180',
                    )}
                  />
                </button>
                {closedExpanded && (
                  <div className="space-y-3">
                    {loadingClosed ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 size={20} className="animate-spin text-slate-400" />
                      </div>
                    ) : closedTickets.length === 0 ? (
                      <p className="text-sm text-slate-300 text-center py-4">No closed tickets</p>
                    ) : (
                      closedTickets.map((ticket) => (
                        <TicketCard
                          key={ticket.id}
                          ticket={ticket}
                          onClick={() => navigate(`/support/${ticket.id}`)}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
